import { useQuery } from '@tanstack/react-query';

import type {
	LeadsServiceSort,
	LeadsTableRow,
	LeadsTableTab,
	LeadStageType,
	LeadType,
	PaginatedResponse,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { leadsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Search leads parameters
 */
export interface SearchLeadsParams {
	query?: string;
	limit?: number;
	page?: number;
	cursor?: string; // Support cursor-based pagination for Kanban
	sortBy?: LeadsServiceSort;
	facets?: string[];
	filters?: {
		tab?: LeadsTableTab;
		stage?: LeadStageType | LeadStageType[] | string; // Allow string for flexibility
		leadType?: LeadType | LeadType[];
		buyerId?: string;
		email?: string;
		cartTotalRange?: { gte: number; lte?: number };
		dateCreatedRange?: { gte: number; lte?: number };
		lastUpdatedRange?: { gte: number; lte?: number };
		actionRequired?: boolean;
		preQualStatus?: string | string[];
		accountFilter?: string | string[];
		models?: string | string[];
		pipeline?: LeadsTableTab | LeadsTableTab[];
	};
	timezone?: string;
}

/**
 * API function to search leads with optional filters and pagination
 * @param servicesProviderId - Required services provider ID
 * @param params - Optional search parameters
 */
export const searchLeads = async (
	servicesProviderId: string,
	params: SearchLeadsParams = {},
): Promise<PaginatedResponse<LeadsTableRow>> => {
	const timezone = params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const response = await apiClient.post<PaginatedResponse<LeadsTableRow>>(
		leadsEndpoints.search(servicesProviderId),
		{ ...params, timezone },
	);
	return response.data;
};

/**
 * Hook for searching leads with optional filters and pagination
 * @param params - Search parameters including filters, pagination, sorting
 * @example
 * ```tsx
 * const { data, isLoading } = useSearchLeads({
 *   filters: { stage: 'TO_REACH_OUT', tab: LeadsTableTab.INBOUND },
 *   limit: 20,
 *   cursor: 'abc123',
 * });
 * ```
 */
export const useSearchLeads = (params: SearchLeadsParams = {}) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.leads.list(servicesProviderId, params).queryKey,
		queryFn: () => searchLeads(servicesProviderId, params),
		enabled: Boolean(servicesProviderId),
		// Use Infinity for user-driven searches - only invalidate on mutations
		// This prevents unnecessary refetches when switching between tabs or views
		// Cache is invalidated when leads are updated via mutations (stage changes, etc.)
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
